import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { useToast } from '../ui/Toast';
import { formatKpiValue, parseTargetInput, targetInputText } from '../../utils/strategicKpiFormat';

interface Props {
    unit: string;
    /** The stored target (fraction for pct), null = no target. */
    value: number | null;
    canEdit: boolean;
    saving: boolean;
    /** Resolves true when the value is stored (the cell leaves edit mode), false to stay in edit mode. */
    onSave: (value: number | null) => Promise<boolean>;
    title?: string;
    /** Small second line under the value (e.g. "Σ persons 239 500"). */
    secondary?: ReactNode;
    testId?: string;
    /** K5 (S2-08): what the cell is — "Target Visits YTD — Ledeur Michel" — so its name is not just its value. */
    label?: string;
}

/**
 * One editable target. Click (or focus + Enter) opens the box, Enter / blur stores, Escape
 * discards. The box shows the value the way it is typed (pct as a percentage, no thousands
 * separators); the cell shows it formatted. An empty box removes the target — the API audits
 * that like any other change. A value that cannot be parsed is refused with a toast and the
 * box stays open; nothing is ever coerced to 0.
 */
export function TargetCell({ unit, value, canEdit, saving, onSave, title, secondary, testId, label }: Props) {
    const { toast } = useToast();
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const committing = useRef(false);

    useEffect(() => {
        if (editing) { inputRef.current?.focus(); inputRef.current?.select(); }
    }, [editing]);

    const open = () => {
        if (!canEdit || saving) return;
        setText(targetInputText(unit, value));
        setEditing(true);
    };

    const cancel = () => { setEditing(false); };

    const commit = async () => {
        if (committing.current) return;
        const parsed = parseTargetInput(unit, text);
        if (!parsed.ok) { toast(parsed.error, 'error'); inputRef.current?.focus(); return; }
        const same = (parsed.value ?? null) === (value ?? null)
            || (parsed.value !== null && value !== null && Math.abs(parsed.value - value) < 0.00005);
        if (same) { setEditing(false); return; }
        committing.current = true;
        try {
            const ok = await onSave(parsed.value);
            if (ok) setEditing(false); else inputRef.current?.focus();
        } finally {
            committing.current = false;
        }
    };

    if (editing) {
        return (
            <input
                ref={inputRef}
                type="text"
                inputMode="decimal"
                value={text}
                onChange={e => setText(e.target.value)}
                onBlur={() => { void commit(); }}
                onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); void commit(); }
                    else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
                }}
                className="h-8 w-full rounded border border-blue-500 px-1 text-right text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label={label ? `${label} — target value` : 'Target value'}
                data-testid={testId}
            />
        );
    }

    const shown = value === null ? '' : formatKpiValue(unit, value);
    // S1-03: the focus is a visible ring — Enter opens an editor that saves on blur, so the reader
    // must see which person × KPI they are on. S2-08: the name says which cell it is.
    const name = label ? `${label}: ${shown || 'no target'}${canEdit ? ', edit' : ''}` : undefined;
    return (
        <button
            type="button"
            onClick={open}
            disabled={!canEdit || saving}
            title={title ?? (canEdit ? 'Click to edit' : undefined)}
            aria-label={name}
            className={`flex min-h-8 w-full flex-col items-end justify-center rounded px-1 py-0.5 text-right tabular-nums ${canEdit ? 'hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500' : 'cursor-default'} ${value === null ? 'text-gray-600' : 'text-gray-900'}`}
            data-testid={testId}
        >
            <span className="inline-flex items-center gap-1">
                {saving && <Loader2 size={12} className="animate-spin text-blue-500" aria-hidden="true" />}
                {shown || (canEdit ? '＋' : '—')}
            </span>
            {secondary && <span className="text-[11px] font-normal text-gray-600">{secondary}</span>}
        </button>
    );
}
